import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Edit2, Download, CheckCircle, Store, ExternalLink, Share2, Package } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { useAuth } from '../contexts/AuthContext';


export const ProductPreviewScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activePlatform, setActivePlatform] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`, {
        
      });
      
      if (!res.data || !res.data.product) {
        throw new Error('Invalid product data');
      }
      
      const productData = res.data.product;
      setProduct(productData);
      setEditTitle(productData.ai_title || '');
      setEditDesc(productData.ai_description || '');
      setEditPrice(productData.price || '');
      setLoading(false);
    } catch (error) {
      console.error('Product fetch error:', error);
      toast.error('Failed to load product. Please try again.');
      setLoading(false);
      // Navigate back after error
      setTimeout(() => navigate('/my-products'), 2000);
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/products/${productId}`, {
        title: editTitle,
        description: editDesc,
        price: parseFloat(editPrice) || null
      }, {  });
      toast.success('Product updated');
      setEditing(false);
      fetchProduct();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleExport = async (platform) => {
    // Navigate to export success screen
    navigate('/export-success', {
      state: {
        platform,
        productId,
        fileName: `${product?.ai_title?.substring(0, 20).replace(/[^a-z0-9]/gi, '_') || 'product'}_${platform}.csv`
      }
    });
  };

  const handlePublish = async () => {
    try {
      await api.post(`/products/${productId}/publish`, {}, {  });
      toast.success('Product marked as published!');
      fetchProduct();
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const handlePublishToShopify = async () => {
    setPublishing(true);
    try {
      const res = await api.post(
        `/shopify/publish/${productId}`,
        {},
        {
          
        }
      );

      if (res.data.success) {
        if (res.data.mock) {
          toast.success('✓ Product ready for Shopify! Connect your store in settings to publish directly.', {
            duration: 6000
          });
        } else {
          toast.success(`✓ Product published to Shopify! View it at ${res.data.product_url}`, {
            duration: 8000
          });
        }
        fetchProduct();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Please connect your Shopify store first', {
          action: {
            label: 'Connect Now',
            onClick: () => navigate('/shopify-connect')
          }
        });
      } else {
        toast.error('Failed to publish to Shopify');
      }
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <LoadingOverlay message="Loading product..." />;
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">
            Product not found
          </h2>
          <p className="text-base text-slate-600 mb-6">
            This product doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate('/my-products')}
            className="w-full h-14 bg-[#128C7E] text-white rounded-xl text-lg font-bold active:scale-95 transition-transform shadow-md"
          >
            Go to My Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar 
          title="Product Preview" 
          showBack={true}
          action={
            <button
              onClick={() => setEditing(!editing)}
              className="text-white"
              data-testid="edit-toggle-button"
            >
              <Edit2 size={20} />
            </button>
          }
        />

        <div className="p-6 space-y-6">
          {/* Image Gallery */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              {product.images && product.images[0] ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${product.images[0].path}`}
                  alt="Product"
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              ) : (
                <div className="text-center text-gray-400">
                  <Package size={64} className="mx-auto mb-2" />
                  <p>No image</p>
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 p-2">
                {product.images.slice(1, 5).map((img, idx) => (
                  <img
                    key={idx}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img.path}`}
                    alt={`Thumbnail ${idx + 2}`}
                    className="aspect-square rounded-lg object-cover"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          {editing ? (
            <div className="space-y-4" data-testid="edit-form">
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-3">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none"
                  data-testid="edit-title-input"
                  placeholder="Enter product title"
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-3">
                  Description
                </label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none resize-none"
                  data-testid="edit-description-input"
                  placeholder="Enter product description"
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-3">
                  Price (₹)
                </label>
                <input
                  type="number"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none"
                  data-testid="edit-price-input"
                  placeholder="0"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdate}
                  disabled={!editTitle || !editDesc}
                  className="flex-1 h-14 bg-[#128C7E] text-white rounded-xl font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-changes-button"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 h-14 bg-gray-200 text-slate-800 rounded-xl font-bold active:scale-95 transition-transform"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="product-details">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="product-title">
                  {product.ai_title}
                </h2>
                {product.price && (
                  <p className="text-3xl font-bold text-[#128C7E]" data-testid="product-price">
                    ₹{product.price}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-base font-bold text-slate-500 uppercase tracking-wide mb-3">
                  Description
                </h3>
                <p className="text-base text-slate-600 leading-relaxed" data-testid="product-description">
                  {product.ai_description}
                </p>
              </div>

              {product.ai_bullets && product.ai_bullets.length > 0 && (
                <div>
                  <h3 className="text-base font-bold text-slate-500 uppercase tracking-wide mb-3">
                    Key Features
                  </h3>
                  <ul className="space-y-3">
                    {product.ai_bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-base text-slate-600">
                        <span className="text-[#128C7E] mt-1 text-lg">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {product.ai_tags && product.ai_tags.length > 0 && (
                <div>
                  <h3 className="text-base font-bold text-slate-500 uppercase tracking-wide mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.ai_tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-green-50 text-[#128C7E] rounded-full text-base font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {!editing && (
            <div className="space-y-3">
              {/* Edit Product Details */}
              <button
                onClick={() => navigate(`/product-edit/${productId}`)}
                className="w-full h-12 bg-white border-2 border-purple-200 text-purple-700 rounded-xl text-base font-bold flex items-center justify-center gap-2 transition-transform active:scale-95"
                data-testid="edit-product-btn"
              >
                <Edit2 size={18} />
                Edit Product Details
              </button>

              {/* Main Sell Everywhere Button */}
              <button
                onClick={() => setShowExport(!showExport)}
                className="w-full h-16 bg-[#25D366] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg shadow-green-200"
                data-testid="sell-everywhere-button"
              >
                <Share2 size={24} />
                Sell Everywhere
              </button>

              {showExport && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-200"
                  data-testid="export-options"
                >
                  {/* Platform Tabs */}
                  <div className="grid grid-cols-4 gap-2" data-testid="platform-tabs">
                    {[
                      { id: 'amazon', label: 'Amazon', color: '#FF9900', sellerUrl: 'https://sellercentral.amazon.in/listing/upload' },
                      { id: 'flipkart', label: 'Flipkart', color: '#2874F0', sellerUrl: 'https://seller.flipkart.com/index.html#dashboard/listings-add' },
                      { id: 'etsy', label: 'Etsy', color: '#F1641E', sellerUrl: 'https://www.etsy.com/your/shops/me/tools/listings/create' },
                      { id: 'shopify', label: 'Shopify', color: '#96BF48', sellerUrl: null }
                    ].map(p => (
                      <button
                        key={p.id}
                        onClick={() => setActivePlatform(activePlatform === p.id ? null : p.id)}
                        className={`py-3 px-2 rounded-lg text-sm font-bold transition-all ${
                          activePlatform === p.id 
                            ? 'text-white shadow-md scale-105' 
                            : 'bg-white text-slate-700 border border-gray-200'
                        }`}
                        style={activePlatform === p.id ? { backgroundColor: p.color } : {}}
                        data-testid={`tab-${p.id}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {/* Amazon Panel */}
                  {activePlatform === 'amazon' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-orange-200">
                        <h4 className="font-bold text-slate-800 mb-2">Sell on Amazon</h4>
                        <p className="text-sm text-slate-500 mb-3">Download your product CSV and upload it to Amazon Seller Central</p>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleExport('amazon')}
                            className="w-full h-12 bg-[#FF9900] text-white rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="download-amazon-csv"
                          >
                            <Download size={18} /> Download Amazon CSV
                          </button>
                          <a
                            href="https://sellercentral.amazon.in/listing/upload"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-12 bg-white border-2 border-[#FF9900] text-[#FF9900] rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="open-amazon-seller"
                          >
                            <ExternalLink size={18} /> Open Amazon Seller Central
                          </a>
                        </div>
                        <div className="mt-3 bg-orange-50 rounded-lg p-3">
                          <p className="text-xs text-orange-800 font-semibold mb-1">Quick Steps:</p>
                          <ol className="text-xs text-orange-700 space-y-1 list-decimal pl-3">
                            <li>Download the CSV file above</li>
                            <li>Go to Amazon Seller Central &gt; Add Products via Upload</li>
                            <li>Upload the CSV file and submit</li>
                          </ol>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Flipkart Panel */}
                  {activePlatform === 'flipkart' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-slate-800 mb-2">Sell on Flipkart</h4>
                        <p className="text-sm text-slate-500 mb-3">Download your product CSV and upload it to Flipkart Seller Hub</p>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleExport('flipkart')}
                            className="w-full h-12 bg-[#2874F0] text-white rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="download-flipkart-csv"
                          >
                            <Download size={18} /> Download Flipkart CSV
                          </button>
                          <a
                            href="https://seller.flipkart.com/index.html#dashboard/listings-add"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-12 bg-white border-2 border-[#2874F0] text-[#2874F0] rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="open-flipkart-seller"
                          >
                            <ExternalLink size={18} /> Open Flipkart Seller Hub
                          </a>
                        </div>
                        <div className="mt-3 bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-800 font-semibold mb-1">Quick Steps:</p>
                          <ol className="text-xs text-blue-700 space-y-1 list-decimal pl-3">
                            <li>Download the CSV file above</li>
                            <li>Go to Flipkart Seller Hub &gt; Add Listings</li>
                            <li>Upload the CSV file and submit</li>
                          </ol>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Etsy Panel */}
                  {activePlatform === 'etsy' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-orange-200">
                        <h4 className="font-bold text-slate-800 mb-2">Sell on Etsy</h4>
                        <p className="text-sm text-slate-500 mb-3">Download your product CSV and upload it to your Etsy Shop</p>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleExport('etsy')}
                            className="w-full h-12 bg-[#F1641E] text-white rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="download-etsy-csv"
                          >
                            <Download size={18} /> Download Etsy CSV
                          </button>
                          <a
                            href="https://www.etsy.com/your/shops/me/tools/listings/create"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-12 bg-white border-2 border-[#F1641E] text-[#F1641E] rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="open-etsy-seller"
                          >
                            <ExternalLink size={18} /> Open Etsy Shop Manager
                          </a>
                        </div>
                        <div className="mt-3 bg-orange-50 rounded-lg p-3">
                          <p className="text-xs text-orange-800 font-semibold mb-1">Quick Steps:</p>
                          <ol className="text-xs text-orange-700 space-y-1 list-decimal pl-3">
                            <li>Download the CSV file above</li>
                            <li>Go to Etsy Shop Manager &gt; Listings</li>
                            <li>Use "Add a listing" or bulk upload</li>
                          </ol>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Shopify Panel */}
                  {activePlatform === 'shopify' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <h4 className="font-bold text-slate-800 mb-2">Sell on Shopify</h4>
                        <p className="text-sm text-slate-500 mb-3">Publish directly to your Shopify store with one tap</p>
                        <div className="space-y-2">
                          <button
                            onClick={handlePublishToShopify}
                            disabled={publishing}
                            className="w-full h-12 bg-[#96BF48] text-white rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
                            data-testid="publish-shopify-direct"
                          >
                            <Store size={18} /> {publishing ? 'Publishing...' : 'Publish to Shopify'}
                          </button>
                          <button
                            onClick={() => navigate('/shopify-connect')}
                            className="w-full h-12 bg-white border-2 border-[#96BF48] text-[#5E8E3E] rounded-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            data-testid="connect-shopify-store"
                          >
                            <ExternalLink size={18} /> Connect/Manage Store
                          </button>
                        </div>
                        <div className="mt-3 bg-green-50 rounded-lg p-3">
                          <p className="text-xs text-green-800 font-semibold mb-1">Quick Steps:</p>
                          <ol className="text-xs text-green-700 space-y-1 list-decimal pl-3">
                            <li>First connect your Shopify store (one-time)</li>
                            <li>Then tap "Publish to Shopify" for direct upload</li>
                            <li>Your product goes live instantly!</li>
                          </ol>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {!activePlatform && (
                    <p className="text-sm text-[#667781] text-center py-2">
                      Tap a platform above to get started
                    </p>
                  )}
                </motion.div>
              )}

              <button
                onClick={handlePublish}
                className="w-full h-14 bg-white border-2 border-[#128C7E] text-[#128C7E] rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition-transform active:scale-95"
                data-testid="mark-published-button"
              >
                <CheckCircle size={20} />
                Mark as Published
              </button>

              {/* Marketing Button */}
              <button
                onClick={() => navigate(`/marketing/${productId}`)}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition-transform active:scale-95 shadow-md"
                data-testid="marketing-button"
              >
                Promote on Instagram
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
